import React from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPanel,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Input,
  toastManager,
} from "@salestrak/ui";

import { useAppData, type WorkflowRow } from "#app/lib/data";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { resultMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";

type RenameWorkflowDialogProps = Omittable<{
  /** The dialog reads only identity + name, so any workflow row shape (the
   *  list row, the workspace row) can drive it. Null keeps it closed. */
  workflow: Pick<WorkflowRow, "workflowId" | "name"> | null;
  onOpenChange: (open: boolean) => void;
}>;

/**
 * Rename prompt for a workflow — the menu's Rename action on surfaces whose
 * row cannot swap to an inline input (the workflows tables). One AlertDialog
 * at every breakpoint.
 */
export function RenameWorkflowDialog({
  workflow,
  onOpenChange,
}: RenameWorkflowDialogProps) {
  const { updateWorkflow } = useWorkflowMutations();
  const { refetch: refetchAppData } = useAppData();
  const [draft, setDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // Seed the draft from the target as the dialog opens; keyed on identity so
  // re-opening on another row never shows the previous row's draft.
  const workflowId = workflow?.workflowId ?? null;
  React.useEffect(() => {
    setDraft(workflow?.name ?? "");
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the name seeds once per target; live cache renames must not clobber an open draft.
  }, [workflowId]);

  const commit = async () => {
    if (!workflowId) return;
    const name = draft.trim();
    if (!name || name === workflow?.name) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      const result = await updateWorkflow(workflowId, { name });
      // A refusal (a name another workflow holds) keeps the draft open.
      if (result?.__typename !== "Workflow") {
        toastManager.add({
          title: resultMessage(result) ?? "Failed to rename workflow",
          type: "error",
        });
        return;
      }
      // The sidebar rows and the crumb read the workspace list.
      refetchAppData();
      onOpenChange(false);
    } catch (err) {
      toastMutationError(err, "Failed to rename workflow");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open={workflow != null} onOpenChange={onOpenChange}>
      <AlertDialogPopup
        render={
          <Box
            as="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.trim()) void commit();
            }}
          />
        }
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Rename workflow</AlertDialogTitle>
          <AlertDialogDescription>
            Enter a new name for this workflow.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogPanel>
          <Input
            aria-label="Workflow name"
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            value={draft}
          />
        </AlertDialogPanel>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          <Button
            disabled={saving || !draft.trim()}
            loading={saving}
            type="submit"
          >
            Rename
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
